import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLazyGetResidentListQuery } from '@/store/api';
import { 
  IResidentListResponse, 
  IFilterModel,
  ISortModel,
  IPaginationModel,
} from '@/types/admin/residentList';
import { useAdminUser } from './useAdminUser';
import { normalizeResidentRow } from '@/utils/admin/residentsList/normalize';
import { useDebouncedValue } from '@/hooks/common/useDebouncedValue';
import { handleApiError, EnhancedError } from '@/utils/admin/errorHandling';
import { DEFAULT_RESIDENT_FILTER_MODEL, DEFAULT_RESIDENT_SORT_MODEL, DEFAULT_PAGINATION_MODEL } from '@/constants/admin/residents';
import { useDataTableState } from '@/contexts/admin/DataTableContext';

// Normalization moved to shared util

export const useResidentList = () => {
  const adminUser = useAdminUser();
  
  // Table UI state (global) with defaults applied on first use
  const table = useDataTableState('admin:residents');
  const filterModel = (
    table.filterModel?.items && table.filterModel.items.length > 0
      ? table.filterModel
      : DEFAULT_RESIDENT_FILTER_MODEL
  ) as IFilterModel;
  const sortModel = (table.sortModel?.length ? table.sortModel : (DEFAULT_RESIDENT_SORT_MODEL as ISortModel[])) as ISortModel[];
  const paginationModel = (table.paginationModel?.pageSize ? table.paginationModel : DEFAULT_PAGINATION_MODEL) as IPaginationModel;
  const searchQuery = table.searchQuery;
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);

  // Error handling state
  const [enhancedError, setEnhancedError] = useState<EnhancedError | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);

  // RTK Query hook (lazy query for manual triggering like admin-web)
  const [getResidentList, { 
    data, 
    isLoading, 
    isFetching, 
    isUninitialized, 
    error 
  }] = useLazyGetResidentListQuery();

  /**
   * Enhanced trigger fetch with error handling
   */
  const triggerFetch = useCallback(async () => {
    if (!adminUser.selectedPropertyId) {
      console.warn('No property selected, cannot fetch resident list');
      return;
    }

    // Clear previous error
    setEnhancedError(null);

    // Add search query to filter model if present
    const enhancedFilterModel: IFilterModel = {
      items: [
        ...filterModel.items,
        ...(debouncedSearchQuery ? [{ field: "name", operator: "contains", value: debouncedSearchQuery }] : [])
      ]
    };

    try {
      const result = await getResidentList({
        sortModel,
        filterModel: enhancedFilterModel,
        paginationModel,
        selectedPropertyId: adminUser.selectedPropertyId!,
      }).unwrap();
      
      // Reset retry count on success
      setRetryCount(0);
    } catch (fetchError) {
      // Handle and log the error
      const errorDetails = handleApiError(fetchError, '/api/v1/residents/list', retryCount);
      setEnhancedError(errorDetails);
      setRetryCount(prev => prev + 1);
      
      console.error('🔴 Failed to fetch resident list after retries:', errorDetails);
    }
  }, [
    adminUser.selectedPropertyId,
    sortModel,
    filterModel,
    paginationModel,
    debouncedSearchQuery,
    getResidentList
  ]);

  /**
   * Safe data access with default values
   */
  const residentListData: IResidentListResponse = useMemo(() => {
    if (!data || !data.count || !data.rows) {
      return { count: 0, rows: [] };
    }
    return { count: data.count, rows: data.rows };
  }, [data]);

  /**
   * Normalized table rows for display
   */
  const tableRows = useMemo(() => {
    return residentListData?.rows?.map(normalizeResidentRow) || [];
  }, [residentListData]);

  /**
   * Loading state computation - only show loading on initial load or when no data exists
   */
  const isTableLoading = (isLoading || isUninitialized) && (residentListData.rows.length === 0);

  /**
   * Debounced pagination reset for search changes to avoid duplicate fetches.
   * The actual fetch is triggered by the pagination effect.
   */
  useEffect(() => {
    if (!adminUser.selectedPropertyId) return;
    if (paginationModel.page !== 0) {
      table.setPagination({ ...paginationModel, page: 0 });
    }
  }, [debouncedSearchQuery, adminUser.selectedPropertyId]);

  // Removed immediate fetch on filter/sort change to avoid double-fetches.

  /**
   * Fetch data when pagination changes (immediate, no debounce needed)
   */
  useEffect(() => {
    if (adminUser.selectedPropertyId) {
      triggerFetch();
    }
  }, [paginationModel, triggerFetch, adminUser.selectedPropertyId]);

  /**
   * When filters, sort, or search change:
   * - If already on page 0, trigger a fetch immediately (to mirror admin-web behavior)
   * - Otherwise, reset pagination to page 0 (pagination effect will trigger fetch)
   */
  useEffect(() => {
    if (!adminUser.selectedPropertyId) return;
    if (paginationModel.page === 0) {
      triggerFetch();
    } else {
      table.setPagination({ ...paginationModel, page: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterModel, sortModel, debouncedSearchQuery, adminUser.selectedPropertyId]);

  // Note: search pagination reset is handled by the debounced effect above

  /**
   * Update filter model
   */
  const updateFilterModel = useCallback((newFilterModel: IFilterModel) => {
    table.setFilter(newFilterModel);
  }, [table]);

  /**
   * Update sort model
   */
  const updateSortModel = useCallback((newSortModel: ISortModel[]) => {
    table.setSort(newSortModel);
  }, [table]);

  /**
   * Update pagination
   */
  const updatePaginationModel = useCallback((newPaginationModel: IPaginationModel) => {
    table.setPagination(newPaginationModel);
  }, [table]);

  /**
   * Update search query
   */
  const updateSearchQuery = useCallback((query: string) => {
    table.setSearch(query);
  }, [table]);

  /**
   * Refresh data manually
   */
  const refresh = useCallback(() => {
    if (adminUser.selectedPropertyId) {
      triggerFetch();
    }
  }, [adminUser.selectedPropertyId, triggerFetch]);

  /**
   * Go to next page
   */
  const goToNextPage = useCallback(() => {
    const maxPage = Math.ceil(residentListData.count / paginationModel.pageSize) - 1;
    if (paginationModel.page < maxPage) {
      table.setPagination({ ...paginationModel, page: paginationModel.page + 1 });
    }
  }, [residentListData.count, paginationModel, table]);

  /**
   * Go to previous page
   */
  const goToPreviousPage = useCallback(() => {
    if (paginationModel.page > 0) {
      table.setPagination({ ...paginationModel, page: paginationModel.page - 1 });
    }
  }, [paginationModel.page, paginationModel, table]);

  /**
   * Check if there's a next page
   */
  const hasNextPage = useMemo(() => {
    const maxPage = Math.ceil(residentListData.count / paginationModel.pageSize) - 1;
    return paginationModel.page < maxPage;
  }, [residentListData.count, paginationModel]);

  /**
   * Check if there's a previous page
   */
  const hasPreviousPage = useMemo(() => {
    return paginationModel.page > 0;
  }, [paginationModel.page]);

  /**
   * Manual retry function for user-initiated retries
   */
  const manualRetry = useCallback(() => {
    setEnhancedError(null);
    triggerFetch();
  }, [triggerFetch]);

  return {
    // Data
    data: residentListData,
    tableRows,
    
    // State
    isLoading: isTableLoading,
    error: error ? String(error) : null,
    
    // Enhanced Error Handling
    enhancedError,
    retryCount,
    canRetry: enhancedError?.shouldRetry || false,
    manualRetry,
    
    // Pagination
    paginationModel,
    updatePaginationModel,
    goToNextPage,
    goToPreviousPage,
    hasNextPage,
    hasPreviousPage,
    
    // Filtering & Sorting
    filterModel,
    updateFilterModel,
    sortModel,
    updateSortModel,
    searchQuery,
    updateSearchQuery,
    
    // Actions
    refresh,
    getResidentList: triggerFetch,
  };
};
